import { Record, Map, List, fromJS } from 'immutable';
import _ from 'lodash';

import { CurrencyCode } from 'utils/currencyFormat';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { RoleGrantSource } from 'generated/graphql';

import Organization from '../organization/model';

export interface FullName {
  first: string;
  last: string;
}

export interface CapBalanceInfo {
  // null means unlimited budget
  balance: number | null;
  currencyCode: CurrencyCode;
  id: string;
  limitPerUser: number;
  name: string;
  contributionBased?: boolean;
}

export const properties = {
  id: '',
  type: '',
  provider: '',
  // We need to keep the defaults to null so it would be clear when the privileges have been loaded
  privileges: null,
  email: '',
  name: '',
  mainPicture: '',
  fullName: {},
  preferredName: null,
  tShirtSize: '',
  organization: new Organization(),
  organizationDepartment: '',
  // by default formResponses are not loaded, they are only fetched
  // on the user profile page
  formResponses: null,
  skills: [],
  displayCurrency: 'USD',
  locations: List(),
  location: null,
  _capBalanceByBudget: [],
  features: undefined,
  jobTitle: '',
  onboarding: Map(),
  roleGrants: [],
  roles: [],
  additionalData: {},
};

export type OnboardingStep =
  | 'verification'
  | 'companyAffiliation'
  | 'payment'
  | 'needHelp'
  | 'presence'
  | 'contact'
  | 'brand';

export interface PopulatedRoleGrant {
  enabled: boolean;
  role: string;
  context: {
    organization: {
      mainPicture: string;
      name: string;
      id: string;
      type: 'Nonprofit' | 'Company';
    };
    locations: [];
    organizationTypes: Array<'Nonprofit' | 'Company'>;
  };
  metadata?: {
    assignmentSource?: RoleGrantSource;
    assignedAt?: string;
  };
}

export default class User extends Record(properties, 'User') {
  public readonly id!: string;

  public readonly type!: string;

  public readonly privileges!: Privileges[];

  public readonly email!: string;

  public readonly name!: string;

  public readonly mainPicture!: string;

  public readonly fullName!: FullName;

  public readonly preferredName!: FullName;

  public readonly tShirtSize!: string;

  public readonly organization!: Organization;

  public readonly organizationDepartment!: string;

  public readonly formResponses!: any;

  public readonly skills!: string[];

  public readonly displayCurrency!: string;

  public readonly locations!: List<any>;

  public readonly _capBalanceByBudget!: CapBalanceInfo[];

  public readonly features?: Map<Features, boolean>;

  public readonly jobTitle!: string;

  public readonly onboarding!: Map<OnboardingStep, boolean>;

  public readonly provider!: string;

  public readonly roles!: Array<{ _id: string; name: string; code?: string }>;

  public readonly roleGrants!: PopulatedRoleGrant[];

  public readonly additionalData!: Map<
    'nonprofitAddressChangeRequests' | 'proofOfAssociation',
    unknown
  >;

  constructor(values: any = {}) {
    const validValues = _.pick(values, Object.keys(properties));
    super({
      ...validValues,
      type: values.__t, // eslint-disable-line no-underscore-dangle
      fullName: Map(values.fullName),
      preferredName: Map(values.preferredName),
      organization: values.organization && new Organization(values.organization),
      locations: List(values.locations),
      features: values.features ? Map(values.features) : undefined,
      additionalData: values.additionalData ? Map(values.additionalData) : undefined,
      onboarding: fromJS(values.onboarding),
    });
  }

  hasPrivilege(privilege: Privileges): boolean {
    return (
      (this.isAdmin && !privilege.startsWith('restricted:')) ||
      (this.privileges && this.privileges.includes(privilege))
    );
  }

  hasFeature(feature: Features): boolean {
    return this.features?.get(feature) === true;
  }

  canDonate(): boolean {
    if (this.organization?.isCompany() && this.features?.get(Features.DONATIONS) === true) {
      return true;
    }

    if (this.organization?.acceptsDonations) {
      return true;
    }

    return !this.organization?.isCompany() && !!this.organization?.stripe.userId;
  }

  get isAdmin(): boolean {
    return this.type === 'Admin';
  }

  get isEmployee(): boolean {
    return this.organization?.isCompany();
  }
}
